import type { AvailabilityState, UnpostedItem } from "~/store/availabilitySlice";

import logger from "~/helpers/logger";

export interface LegacyCountActionItem {
	grade: number;
	itemId: string;
	itemName: string;
	quantity: number;
	reasonCode?: number;
	reasonText?: string;
	sku: string;
	upc: string;
	why?: string;
}

export interface LegacyQueuedAction {
	id: string;
	items?: LegacyCountActionItem[];
	locationId?: string;
	queuedAt: string;
	status: string;
	type: string;
}

export interface LegacyAvailabilityState extends AvailabilityState {
	actionQueue?: LegacyQueuedAction[];
}

const RESTAGEABLE_LEGACY_COUNT_STATUSES = ["failed", "pending", "posting"];

const isRestageableLegacyCount = (action: LegacyQueuedAction): boolean =>
	action.type === "count" && RESTAGEABLE_LEGACY_COUNT_STATUSES.includes(action.status);

const isUnreplayableLegacyAction = (action: LegacyQueuedAction): boolean =>
	action.status !== "posted" && !isRestageableLegacyCount(action);

const toUnpostedItem = (countItem: LegacyCountActionItem): UnpostedItem => ({
	...(countItem.why !== undefined ? { comment: countItem.why } : {}),
	grade: countItem.grade,
	itemId: countItem.itemId,
	name: countItem.itemName,
	quantity: countItem.quantity,
	...(countItem.reasonCode !== undefined ? { reasonCode: countItem.reasonCode } : {}),
	...(countItem.reasonText !== undefined ? { reasonText: countItem.reasonText } : {}),
	sku: countItem.sku,
	upc: countItem.upc
});

const mergeRestagedCounts = (
	unpostedCounts: Record<string, UnpostedItem[]>,
	actionQueue: LegacyQueuedAction[]
): Record<string, UnpostedItem[]> =>
	actionQueue.filter(isRestageableLegacyCount).reduce<Record<string, UnpostedItem[]>>((mergedCounts, action) => {
		const restagedItems = (action.items ?? []).map(toUnpostedItem);
		if (action.locationId === undefined || restagedItems.length === 0) return mergedCounts;
		return {
			...mergedCounts,
			[action.locationId]: [...(mergedCounts[action.locationId] ?? []), ...restagedItems]
		};
	}, unpostedCounts);

const reportUnreplayableLegacyActions = (actionQueue: LegacyQueuedAction[]): void => {
	const unreplayableActions = actionQueue.filter(isUnreplayableLegacyAction);
	if (unreplayableActions.length === 0) return;
	logger.error("[availability] legacy actionQueue entries dropped on upgrade — no frozen idempotency key to replay", {
		droppedActions: unreplayableActions.map(({ id, queuedAt, type }) => ({ id, queuedAt, type }))
	});
};

const migrateLegacyAvailabilityActionQueue = (outboundState: LegacyAvailabilityState): AvailabilityState => {
	const { actionQueue, ...rehydratedState } = outboundState;
	if (actionQueue === undefined) return rehydratedState;
	reportUnreplayableLegacyActions(actionQueue);
	return {
		...rehydratedState,
		unpostedCounts: mergeRestagedCounts(rehydratedState.unpostedCounts ?? {}, actionQueue)
	};
};

export default migrateLegacyAvailabilityActionQueue;
